import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface FollowContextValue {
  following: Set<string>;
  toggle: (userId: string) => Promise<void>;
  isFollowing: (userId: string) => boolean;
}

const FollowContext = createContext<FollowContextValue | null>(null);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function FollowProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) { setFollowing(new Set()); return; }
    let cancelled = false;
    supabase
      .from("follows")
      .select("followed_id")
      .eq("follower_id", user.id)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.error("[follows] load failed:", error); return; }
        setFollowing(new Set((data ?? []).map((r) => r.followed_id)));
      });
    return () => { cancelled = true; };
  }, [user]);

  const toggle = useCallback(async (userId: string) => {
    console.log("[follows] toggle called:", { userId, followerId: user?.id });

    if (!user) { console.error("[follows] no current user"); return; }

    if (!userId || !UUID_RE.test(userId)) {
      console.error("[follows] invalid target id — expected UUID, got:", userId);
      return;
    }

    const isOn = following.has(userId);

    // Optimistic update
    setFollowing((prev) => {
      const next = new Set(prev);
      if (isOn) next.delete(userId);
      else next.add(userId);
      return next;
    });

    if (isOn) {
      const { error } = await supabase
        .from("follows")
        .delete()
        .eq("follower_id", user.id)
        .eq("followed_id", userId);
      if (error) {
        console.error("[follows] unfollow failed:", error.message, error);
        setFollowing((prev) => new Set(prev).add(userId)); // rollback
      } else {
        console.log("[follows] unfollowed:", userId);
      }
    } else {
      // Upsert with ignoreDuplicates so a pre-existing row never causes a
      // rollback — the user is already following, which is the desired state.
      const { error } = await supabase
        .from("follows")
        .upsert(
          { follower_id: user.id, followed_id: userId },
          { onConflict: "follower_id,followed_id", ignoreDuplicates: true },
        );
      if (error) {
        console.error("[follows] follow failed:", error.message, error);
        setFollowing((prev) => { const next = new Set(prev); next.delete(userId); return next; }); // rollback
      } else {
        console.log("[follows] followed:", userId);
      }
    }
  }, [user, following]);

  const isFollowing = useCallback((userId: string) => following.has(userId), [following]);

  return (
    <FollowContext.Provider value={{ following, toggle, isFollowing }}>{children}</FollowContext.Provider>
  );
}

export function useFollow(): FollowContextValue {
  const ctx = useContext(FollowContext);
  if (!ctx) throw new Error("useFollow must be used inside FollowProvider");
  return ctx;
}
